import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function ActivatePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, invite: null, err: "" });
  const [form, setForm] = useState({ email: "", password: "", confirm: "", full_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const { activate } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setState({ loading: false, err: "رابط التفعيل مفقود" }); return; }
    api.post("/auth/activate/verify", { token })
      .then((r) => {
        setState({ loading: false, invite: r.data, err: "" });
        setForm((f) => ({ ...f, email: r.data.invited_email || "" }));
      })
      .catch((e) => setState({ loading: false, err: extractError(e, "الرابط غير صالح") }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("كلمة المرور يجب ألا تقل عن 8 أحرف"); return; }
    if (form.password !== form.confirm) { toast.error("كلمتا المرور غير متطابقتين"); return; }
    if (!form.full_name.trim()) { toast.error("أدخل اسمك الكامل"); return; }
    setSubmitting(true);
    try {
      const u = await activate(token, form.email, form.password, form.full_name);
      toast.success(`أهلًا ${u.full_name}، تم إنشاء حسابك!`);
      nav("/dashboard");
    } catch (err) {
      toast.error(extractError(err, "فشل تفعيل الحساب"));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return (
    <div className="min-h-screen grid place-items-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
  if (state.err) return (
    <div className="min-h-screen grid place-items-center bg-background p-4 sm:p-6">
      <div className="max-w-md sm:max-w-lg w-full text-center space-y-4 bg-card p-6 sm:p-10 rounded-2xl border shadow-sm">
        <h1 className="text-xl sm:text-2xl font-display font-semibold text-destructive">{state.err}</h1>
        <p className="text-muted-foreground text-sm sm:text-base">تواصل مع الدعم للحصول على رابط جديد.</p>
        <Link to="/"><Button variant="outline">العودة للرئيسية</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid place-items-center bg-background p-4 sm:p-6">
      <div className="max-w-md sm:max-w-lg w-full space-y-6 bg-card p-6 sm:p-10 rounded-2xl border shadow-sm">
        <div className="text-center">
          <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center font-display font-bold text-xl mx-auto mb-4">م</div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">إكمال إنشاء الحساب</h1>
          {state.invite?.product_name && (
            <p className="text-sm text-muted-foreground mt-2">اشتراكك: <strong className="text-accent">{state.invite.product_name}</strong></p>
          )}
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
          <div className="space-y-2">
            <Label>الاسم الكامل</Label>
            <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="activate-name" />
          </div>
          <div className="space-y-2">
            <Label>البريد الإلكتروني للحساب</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="activate-email" />
            <p className="text-xs text-muted-foreground">يمكنك استخدام بريد مختلف عن بريد الشراء.</p>
          </div>
          <div className="space-y-2">
            <Label>كلمة المرور</Label>
            <Input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="activate-password" />
          </div>
          <div className="space-y-2">
            <Label>تأكيد كلمة المرور</Label>
            <Input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="activate-password-confirm" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full rounded-full" data-testid="activate-submit">
            {submitting ? "جارٍ الإنشاء..." : "إنشاء الحساب وتفعيله"}
          </Button>
        </form>
      </div>
    </div>
  );
}
